import { useState } from "react";
import { ArrowUpRight, Newspaper, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Reveal } from "@/components/site/Motion";

function Clipping({ item, onOpen }) {
  if (!item.clipping_image) {
    return (
      <div
        data-testid={`press-clipping-pending-${item.id}`}
        className="flex aspect-[3/4] w-full flex-col items-center justify-center gap-3 border border-dashed border-obsidian/25 bg-paper text-center"
      >
        <Newspaper size={22} className="text-slategray" />
        <p className="px-6 text-xs font-bold uppercase tracking-[0.18em] text-slategray">Clipping to be added</p>
      </div>
    );
  }
  return (
    <button
      type="button"
      data-testid={`press-clipping-open-${item.id}`}
      onClick={onOpen}
      className="group relative block w-full overflow-hidden border border-ash bg-white p-3 pb-12 text-left"
      aria-label={`View original clipping: ${item.headline}`}
    >
      <img
        src={item.clipping_image}
        alt={`${item.publication} clipping — ${item.headline}`}
        loading="lazy"
        className="aspect-[3/4] w-full object-contain object-top grayscale transition-transform duration-700 ease-out group-hover:scale-[1.03]"
      />
      <span className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-obsidian/85 px-4 py-3 text-xs font-bold uppercase tracking-[0.16em] text-paper backdrop-blur-sm">
        View original clipping <ArrowUpRight size={14} />
      </span>
    </button>
  );
}

export default function PressRow({ item, index }) {
  const [open, setOpen] = useState(false);
  const num = String(index + 1).padStart(2, "0");

  return (
    <Reveal delay={0.04} className="border-t border-ash">
      <article data-testid={`press-item-${item.id}`} className="grid gap-8 py-12 md:grid-cols-12 md:gap-10 md:py-16">
        <div className="md:col-span-1">
          <span className="font-serif text-2xl text-progress">{num}</span>
        </div>

        <div className="md:col-span-7">
          <p className="text-[13px] font-bold uppercase tracking-[0.2em] text-slategray">
            <span className="text-obsidian">{item.publication}</span>
            {item.date_label && <span className="mx-3 text-ash">•</span>}
            {item.date_label}
          </p>
          <h3 className="mt-4 font-serif text-3xl font-medium leading-tight tracking-tight md:text-4xl">{item.headline}</h3>
          {item.summary && <p className="mt-5 max-w-2xl text-base leading-relaxed text-obsidian/70">{item.summary}</p>}
          {item.byline && <p className="mt-5 text-[12px] italic text-slategray">{item.byline}</p>}
          {item.focus && (
            <p className="mt-6 text-sm">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-slategray">Coverage focus</span>
              <span className="mt-1 block text-obsidian/80">{item.focus}</span>
            </p>
          )}
          {item.source_url && (
            <a
              href={item.source_url}
              target="_blank"
              rel="noreferrer"
              data-testid={`press-source-${item.id}`}
              className="mt-6 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-progress hover:text-progress-dark"
            >
              Read at source <ArrowUpRight size={14} />
            </a>
          )}
        </div>

        <div className="md:col-span-4 md:pl-6">
          <Clipping item={item} onOpen={() => setOpen(true)} />
        </div>
      </article>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid={`press-clipping-dialog-${item.id}`}
          className="max-h-[92vh] w-[min(96vw,1100px)] max-w-none overflow-y-auto rounded-none border-0 bg-paper p-0 [&>button]:hidden"
        >
          <div className="flex items-start justify-between gap-6 border-b border-ash px-6 py-4">
            <div>
              <p className="text-[13px] font-bold uppercase tracking-[0.2em] text-slategray">
                {item.publication}
                {item.date_label ? ` • ${item.date_label}` : ""}
              </p>
              <DialogTitle className="mt-1 font-serif text-2xl font-medium tracking-tight">{item.headline}</DialogTitle>
            </div>
            <button
              type="button"
              data-testid={`press-clipping-close-${item.id}`}
              onClick={() => setOpen(false)}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center border border-obsidian/15 hover:border-obsidian"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
          <img src={item.clipping_image} alt={`${item.publication} clipping — ${item.headline}`} className="w-full" />
        </DialogContent>
      </Dialog>
    </Reveal>
  );
}
